import { autoinject, customElement } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MdcMenu } from '@aurelia-mdc-web/menu';
import { AlertService, IPromptDialogData } from '@aurelia-toolkit/alert';
import { DynTableColumnDrag } from './dyn-table-column-drag';
import { DynTableColumnResize } from './dyn-table-column-resize';

/** Detail of the bubbling `column-reorder` event (`from`/`to` absent on reset). `order` is
 *  the resulting visual order of column keys: the header's id, or `#<index>` (authored
 *  position) for headers without one. */
export interface IColumnReorderDetail {
  from?: number;
  to?: number;
  order: string[];
}

/** Detail of the bubbling `column-visibility` event (`column`/`visible` absent on reset). */
export interface IColumnVisibilityDetail {
  column?: string;
  visible?: boolean;
  hidden: string[];
}

/** A complete column configuration: widths and caption overrides by column key, visual
 *  order, hidden keys (a column absent from `captions` keeps its authored caption).
 *  Also the shape of `settings` (the designed, DB-persisted configuration) and of the
 *  `column-settings-changed` event detail. */
export interface IColumnSettings {
  widths?: Record<string, number>;
  order?: string[];
  hidden?: string[];
  captions?: Record<string, string>;
}

type StoredColumnSettings = IColumnSettings;
type HideableColumn = { key: string; label: string; visible: boolean };

/**
 * Wraps a consumer-authored <table> (plain or inside mdc-data-table) and adds column
 * reordering, visibility, and resizing - a port of the aurelia-headless ui-table column
 * features. Every header is movable (drag to reorder), hideable (right-click the header
 * for visibility toggles), and resizable (drag the edge handle) by
 * default - opt out per header with `movable="false"`, `hideable="false"`,
 * `resizable="false"`. Headers are also renamable through the context menu
 * (`renamable="false"` opts out); a caption override lives in `captions`, the authored
 * text is the fallback. `default-hidden` marks an authored visibility default (stored
 * user state wins); `data-col-id` (or `id`) gives a stable persistence key.
 * The table owns the permutation: it moves the rendered
 * cells of every matching row and re-applies the layout to rows the repeater creates
 * later; widths render as scoped nth-child CSS. Rows with a different cell count
 * (colspan summaries) keep their authored layout.
 *
 * Persistence has two tiers. `settings` carries the designed configuration (persisted to
 * the DB with the widget config): it overrides the authored markup and is what "Reset
 * columns" restores. The user tier holds adjustments as a diff against the designed
 * configuration, kept in one of two places: `storage-key` stores it in localStorage, or
 * `user-settings` (a bound IColumnSettings) makes the owner responsible - reads come from
 * the binding and changes are emitted as the bubbling `column-user-settings-changed`
 * event for the owner to persist (e.g. per customer in the DB). Designer surfaces bind
 * `settings` alone and save the `column-settings-changed` event detail (the complete
 * effective configuration) to the widget config.
 */
let nextTableId = 0;

@customElement('dyn-table')
@autoinject
export class DynTable {
  columnMenu: MdcMenu;
  readonly tableId = `dyn-table-${++nextTableId}`;

  @bindable
  storageKey?: string;

  /** The designed (DB-persisted) configuration: the defaults this table starts from and
   *  resets to. Assign a new object to change it - in-place mutation is not observed. */
  @bindable
  settings?: IColumnSettings;
  settingsChanged(): void {
    this.applyInitialColumnLayout();
    this.refreshHideableColumns();
  }

  /** End-user tier supplied by the owner (e.g. per-customer, DB-backed). When defined it
   *  replaces localStorage: reads come from it, and user changes are emitted as the
   *  bubbling `column-user-settings-changed` event (the diff vs designed defaults)
   *  instead of being written to localStorage. */
  @bindable
  userSettings?: IColumnSettings;
  userSettingsChanged(): void {
    this.applyInitialColumnLayout();
    this.refreshHideableColumns();
  }

  /** Visual column order as authored column indexes; [] until a reorder is applied. */
  private columnOrder: number[] = [];
  /** Hidden columns as authored column indexes. */
  private hiddenColumns = new Set<number>();
  /** Authored visibility defaults (`default-hidden` on headers). */
  private defaultHidden = new Set<number>();
  hiddenColumnKeys: string[] = [];
  /** Hideable columns in visual order, backing the header context menu's toggles. */
  hideableColumns: HideableColumn[] = [];
  private layoutObserver?: MutationObserver;
  private scrollContainer?: HTMLElement;
  /** Designed column order as authored indexes ([] = authored order). */
  private defaultOrder: number[] = [];
  /** Designed widths (from `settings`) and the user's overlay; effective = the merge. */
  private defaultSizes: Record<string, number> = {};
  private userSizes: Record<string, number> = {};
  /** Designed caption overrides and the user's overlay; authored text is the fallback. */
  private defaultCaptions: Record<string, string> = {};
  private userCaptions: Record<string, string> = {};
  /** Authored header texts by key, captured before any caption is applied over them. */
  private readonly authoredCaptions = new Map<string, string>();
  /** The header the context menu was opened on, when it is renamable. */
  renameTarget?: HTMLElement;
  /** The width rules, interpolated into the template's <style>. Plain property (not a
   *  getter) so the binding observes it instead of dirty-checking. */
  columnStyleCss = '';

  constructor(private element: Element, private columnDrag: DynTableColumnDrag, private columnResize: DynTableColumnResize,
    private alertService: AlertService) {
    this.element.setAttribute('data-dyn-table-id', this.tableId);
  }

  attached(): void {
    // Always observe: the consumer's table typically renders after attach (an if.bind
    // while data loads), and its appearance is what triggers layout/handle application.
    this.layoutObserver = new MutationObserver(records => this.onRowsMutated(records));
    this.layoutObserver.observe(this.element, { childList: true, subtree: true });
    this.applyInitialColumnLayout();
    this.refreshHideableColumns();
    this.columnDrag.attach(this);
    this.columnResize.attach(this);
  }

  detached(): void {
    this.columnDrag.detach();
    this.columnResize.detach();
    this.layoutObserver?.disconnect();
    this.layoutObserver = undefined;
    this.scrollContainer = undefined;
  }

  storageKeyChanged(): void {
    this.applyInitialColumnLayout();
    this.refreshHideableColumns();
  }

  get host(): HTMLElement {
    return this.element as HTMLElement;
  }

  private get tableElement(): HTMLTableElement | null {
    return this.element.querySelector('table');
  }

  /** The table's horizontal scroller (e.g. mdc-data-table's container), for drag bounds. */
  get reorderContainer(): HTMLElement {
    if (!this.scrollContainer) {
      let el: HTMLElement | undefined = this.tableElement?.parentElement ?? undefined;
      while (el && this.element.contains(el)) {
        const overflowX = getComputedStyle(el).overflowX;
        if (overflowX === 'auto' || overflowX === 'scroll') {
          break;
        }
        el = el.parentElement ?? undefined;
      }
      this.scrollContainer = el && this.element.contains(el) ? el : this.element as HTMLElement;
    }
    return this.scrollContainer;
  }

  /** Header cells of the table's own first header row, in current visual order. */
  headerCells(): HTMLElement[] {
    const row = this.tableElement?.querySelector(':scope > thead > tr');
    return row ? Array.from(row.children) as HTMLElement[] : [];
  }

  /** Column features are on by default; a header opts out with e.g. movable="false". */
  columnFlag(cell: Element, flag: 'movable' | 'hideable' | 'resizable' | 'renamable'): boolean {
    return cell.getAttribute(flag) !== 'false';
  }

  /** A header's stable key: its `data-col-id`, its id, or `#<authored index>`. */
  columnKey(cell: HTMLElement): string {
    return this.keyOf(cell, this.originalIndexOf(cell, this.headerCells().indexOf(cell)));
  }

  private keyOf(cell: Element, original: number): string {
    return cell.getAttribute('data-col-id') || cell.id || `#${original}`;
  }

  setColumnWidth(key: string, width: number, persist = false): void {
    this.userSizes[key] = Math.round(width);
    this.updateColumnStyle();
    if (persist) {
      this.persistSettings();
      this.emitSettingsChanged();
    }
  }

  resetColumnWidths(): void {
    if (!Object.keys(this.userSizes).length) {
      return;
    }
    this.userSizes = {};
    this.updateColumnStyle();
    this.persistSettings();
    this.emitSettingsChanged();
  }

  onContextMenu(event: MouseEvent): boolean {
    const target = event.target instanceof Element ? event.target : null;
    const thead = target?.closest('thead');
    if (!thead || thead.closest('table') !== this.tableElement) {
      return true;
    }
    event.preventDefault();
    // The column menu replaces any ancestor's context menu on headers (e.g. a widget's
    // excel export menu, which stays available on body cells).
    event.stopPropagation();
    const th = target.closest('th');
    this.renameTarget = th && this.headerCells().includes(th) && this.columnFlag(th, 'renamable') ? th : undefined;
    this.refreshHideableColumns();
    this.columnMenu.setAbsolutePosition(event.pageX, event.pageY);
    this.columnMenu.open = true;
    return false;
  }

  /**
   * Move the column at visual index `from` to insertion point `to` (both among header
   * cells). Cells have no backing array to splice, so the table moves the DOM cells of
   * every matching row (bindings survive node moves) and keeps re-applying the order to
   * rows the repeater creates later.
   */
  moveColumn(from: number, to: number): void {
    const order = this.columnOrder.length ? [...this.columnOrder] : this.headerCells().map((_, index) => index);
    const insert = to > from ? to - 1 : to;
    if (from < 0 || from >= order.length || insert < 0 || insert >= order.length || insert === from) {
      return;
    }
    const [moved] = order.splice(from, 1);
    order.splice(insert, 0, moved);
    this.columnOrder = order;
    this.applyColumnLayoutToAll();
    this.updateColumnStyle();
    this.persistSettings();
    this.refreshHideableColumns();
    this.emit<IColumnReorderDetail>('column-reorder', { from, to: insert, order: this.columnOrderKeys() });
    this.emitSettingsChanged();
  }

  /** Toggle a column's visibility by key (header id, or `#<authored index>`). */
  toggleColumn(key: string): void {
    const byKey = this.columnKeyMap();
    const original = byKey.get(key);
    if (original === undefined) {
      return;
    }
    const hide = !this.hiddenColumns.has(original);
    // Never hide the last visible column: the header would collapse and take the menu with it.
    if (hide && byKey.size - this.hiddenColumns.size <= 1) {
      return;
    }
    if (hide) {
      this.hiddenColumns.add(original);
    } else {
      this.hiddenColumns.delete(original);
    }
    this.updateHiddenColumnKeys();
    this.applyColumnLayoutToAll();
    this.persistSettings();
    this.refreshHideableColumns();
    this.emit<IColumnVisibilityDetail>('column-visibility', { column: key, visible: !hide, hidden: this.hiddenColumnKeys });
    this.emitSettingsChanged();
  }

  resetColumns(): void {
    // stay-open-on-selection keeps the menu up for visibility toggles; reset ends it.
    this.columnMenu.open = false;
    this.resetColumnWidths();
    this.resetColumnOrder();
    this.resetColumnVisibility();
    this.resetColumnCaptions();
    // The resets above restore the DESIGNED defaults (the user tier). The owner of
    // `settings` (a designer surface) handles this event to clear the designed
    // configuration itself, restoring the authored markup.
    this.emit('column-reset', undefined);
  }

  /** Prompt for a new caption for the header the context menu was opened on. */
  async startRename(): Promise<void> {
    this.columnMenu.open = false;
    const th = this.renameTarget;
    if (!th || !th.isConnected) {
      return;
    }
    const key = this.columnKey(th);
    if (!this.authoredCaptions.has(key)) {
      this.authoredCaptions.set(key, this.captionTextOf(th));
    }
    const params: Partial<IPromptDialogData> = {
      required: true,
      text: this.userCaptions[key] ?? this.defaultCaptions[key] ?? this.authoredCaptions.get(key) ?? '',
      label: 'Column name',
      cancelText: 'Cancel',
      okText: 'OK'
    };
    if (await this.alertService.prompt(params)) {
      this.setColumnCaption(key, (params.text ?? '').trim());
    }
  }

  /** Set a column's caption; empty or matching the designed/authored default clears the
   *  user override. */
  setColumnCaption(key: string, caption: string): void {
    const fallback = this.defaultCaptions[key] ?? this.authoredCaptions.get(key) ?? '';
    if (!caption || caption === fallback) {
      delete this.userCaptions[key];
    } else {
      this.userCaptions[key] = caption;
    }
    this.applyCaptions();
    this.refreshHideableColumns();
    this.persistSettings();
    this.emitSettingsChanged();
  }

  resetColumnCaptions(): void {
    if (!Object.keys(this.userCaptions).length) {
      return;
    }
    this.userCaptions = {};
    this.applyCaptions();
    this.refreshHideableColumns();
    this.persistSettings();
    this.emitSettingsChanged();
  }

  resetColumnOrder(): void {
    if (!this.columnOrder.length || this.ordersEqual(this.columnOrder, this.effectiveDefaultOrder())) {
      return;
    }
    this.columnOrder = this.defaultOrder.length ? [...this.defaultOrder] : this.columnOrder.map((_, index) => index);
    this.applyColumnLayoutToAll();
    if (!this.defaultOrder.length) {
      this.columnOrder = [];
    }
    this.updateColumnStyle();
    this.persistSettings();
    this.refreshHideableColumns();
    this.emit<IColumnReorderDetail>('column-reorder', { order: this.columnOrderKeys() });
    this.emitSettingsChanged();
  }

  resetColumnVisibility(): void {
    if (this.setsEqual(this.hiddenColumns, this.defaultHidden)) {
      return;
    }
    this.hiddenColumns = new Set(this.defaultHidden);
    this.updateHiddenColumnKeys();
    this.applyColumnLayoutToAll();
    this.persistSettings();
    this.refreshHideableColumns();
    this.emit<IColumnVisibilityDetail>('column-visibility', { hidden: this.hiddenColumnKeys });
    this.emitSettingsChanged();
  }

  /** Designed defaults (markup, then `settings`) first, then local user diffs on top. */
  private applyInitialColumnLayout(): void {
    if (!this.tableElement) {
      return; // the always-on observer applies the layout when the table appears
    }
    const hadLayout = this.columnOrder.length > 0 || this.hiddenColumns.size > 0;
    this.resolveDesignedDefaults();
    this.columnOrder = this.defaultOrder.length ? [...this.defaultOrder] : [];
    this.hiddenColumns = new Set(this.defaultHidden);
    this.userSizes = {};
    this.userCaptions = {};
    const stored = this.readStoredSettings();
    if (stored.widths && typeof stored.widths === 'object') {
      this.userSizes = stored.widths;
    }
    if (stored.captions && typeof stored.captions === 'object') {
      this.userCaptions = stored.captions;
    }
    const order = Array.isArray(stored.order) ? this.resolveColumnOrder(stored.order) : undefined;
    if (order) {
      this.columnOrder = order;
    }
    if (Array.isArray(stored.hidden)) {
      const byKey = this.columnKeyMap();
      const hidden = new Set(stored.hidden.map(key => byKey.get(key)).filter((value): value is number => value !== undefined));
      // Stale data must not hide everything: the menu to unhide lives in the header.
      if (hidden.size < byKey.size) {
        this.hiddenColumns = hidden;
      }
    }
    this.updateHiddenColumnKeys();
    if (this.columnOrder.length || this.hiddenColumns.size) {
      this.applyColumnLayoutToAll();
    } else if (hadLayout) {
      // A previously applied layout (e.g. settings replaced with an empty one) must be
      // undone: an identity pass sorts stamped rows back to authored order.
      this.columnOrder = this.headerCells().map((_, index) => index);
      this.applyColumnLayoutToAll();
      this.columnOrder = [];
    }
    this.updateColumnStyle();
    this.applyCaptions();
  }

  /**
   * Re-resolve the whole layout after the consumer re-rendered the table or changed its
   * column set (conditional columns): stamps and state describe the old columns, so they
   * are discarded and keys re-resolve against the new ones; stored state whose keys no
   * longer match is dropped by the usual validation.
   */
  refreshColumns(): void {
    this.scrollContainer = undefined;
    this.authoredCaptions.clear();
    this.tableElement?.querySelectorAll('[data-col-index], [data-col-hidden]').forEach(cell => {
      cell.removeAttribute('data-col-index');
      cell.removeAttribute('data-col-hidden');
    });
    this.columnOrder = [];
    this.hiddenColumns.clear();
    this.applyInitialColumnLayout();
    this.refreshHideableColumns();
    this.columnResize.injectHandles();
  }

  /** The designed defaults: authored markup, overridden by DB `settings` when present. */
  private resolveDesignedDefaults(): void {
    this.defaultSizes = this.settings?.widths && typeof this.settings.widths === 'object' ? this.settings.widths : {};
    this.defaultCaptions = this.settings?.captions && typeof this.settings.captions === 'object' ? this.settings.captions : {};
    this.defaultOrder = Array.isArray(this.settings?.order) ? this.resolveColumnOrder(this.settings.order) ?? [] : [];
    this.defaultHidden = this.collectDefaultHidden();
    if (Array.isArray(this.settings?.hidden)) {
      const byKey = this.columnKeyMap();
      const hidden = new Set(this.settings.hidden.map(key => byKey.get(key)).filter((value): value is number => value !== undefined));
      // Designed data must not hide everything either: the unhide menu lives in the header.
      if (hidden.size < byKey.size) {
        this.defaultHidden = hidden;
      }
    }
  }

  /** The designed order, or the authored identity when none is designed. */
  private effectiveDefaultOrder(): number[] {
    return this.defaultOrder.length ? this.defaultOrder : this.headerCells().map((_, index) => index);
  }

  private ordersEqual(a: number[], b: number[]): boolean {
    return a.length === b.length && a.every((value, index) => value === b[index]);
  }

  /** Fired after any user change, with the complete effective configuration - designer
   *  surfaces persist this to the widget config (DB). */
  private emitSettingsChanged(): void {
    this.emit<IColumnSettings>('column-settings-changed', {
      widths: { ...this.defaultSizes, ...this.userSizes },
      order: this.columnOrderKeys(),
      hidden: this.hiddenColumnKeys,
      captions: { ...this.defaultCaptions, ...this.userCaptions }
    });
  }

  /** Header captions: user overlay > designed > authored (captured before overriding). */
  private applyCaptions(): void {
    this.headerCells().forEach((cell, position) => {
      const key = this.keyOf(cell, this.originalIndexOf(cell, position));
      if (!this.authoredCaptions.has(key)) {
        this.authoredCaptions.set(key, this.captionTextOf(cell));
      }
      const caption = this.userCaptions[key] ?? this.defaultCaptions[key] ?? this.authoredCaptions.get(key) ?? '';
      if (this.captionTextOf(cell) !== caption) {
        this.setCaptionText(cell, caption);
      }
    });
  }

  /** The header's own text content, ignoring injected elements (resize handle, input). */
  private captionTextOf(cell: Element): string {
    return Array.from(cell.childNodes)
      .filter(node => node.nodeType === Node.TEXT_NODE)
      .map(node => node.textContent)
      .join('')
      .trim();
  }

  private setCaptionText(cell: Element, caption: string): void {
    for (const node of Array.from(cell.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        node.parentNode?.removeChild(node);
      }
    }
    cell.insertBefore(document.createTextNode(caption), cell.firstChild);
  }

  /** Renders persisted widths as nth-child rules scoped to this element (hidden cells
   *  still occupy their child positions, so indexes stay correct). The overflow clamp is
   *  what lets a cell shrink below its content in an auto-layout table. */
  private updateColumnStyle(): void {
    const sizes = { ...this.defaultSizes, ...this.userSizes };
    const table = `[data-dyn-table-id="${this.tableId}"] table`;
    this.columnStyleCss = this.headerCells()
      .map((cell, position) => {
        const width = sizes[this.columnKey(cell)];
        // Body cells truncate below their set width; headers wrap instead (break-word so
        // even a single long word cannot force the column wider than set).
        return width === undefined
          ? ''
          : `${table} tr > :nth-child(${position + 1}) { box-sizing: border-box; width: ${width}px !important; min-width: ${width}px !important; max-width: ${width}px !important; }`
          + `${table} thead tr > :nth-child(${position + 1}) { white-space: normal; overflow-wrap: break-word; }`
          + `${table} tbody tr > :nth-child(${position + 1}) { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }`;
      })
      .join('');
  }

  private collectDefaultHidden(): Set<number> {
    const cells = this.headerCells();
    const defaults = new Set(cells
      .map((cell, position) => cell.hasAttribute('default-hidden') ? this.originalIndexOf(cell, position) : -1)
      .filter(index => index >= 0));
    // Defaults must not hide everything: the menu to unhide lives in the header.
    return defaults.size < cells.length ? defaults : new Set<number>();
  }

  /** Stored keys -> authored indexes; undefined when the keys no longer match the table. */
  private resolveColumnOrder(keys: string[]): number[] | undefined {
    const byKey = this.columnKeyMap();
    if (!byKey.size || keys.length !== byKey.size) {
      return undefined;
    }
    const order = keys.map(key => byKey.get(key));
    if (order.some(value => value === undefined) || new Set(order).size !== order.length) {
      return undefined;
    }
    return order as number[];
  }

  /** Column key -> authored index, for the current header row. */
  private columnKeyMap(): Map<string, number> {
    return new Map(this.headerCells().map((cell, position) => {
      const original = this.originalIndexOf(cell, position);
      return [this.keyOf(cell, original), original] as const;
    }));
  }

  private originalIndexOf(cell: Element, position: number): number {
    const stamp = cell.getAttribute('data-col-index');
    return stamp !== null ? Number(stamp) : position;
  }

  private columnOrderKeys(): string[] {
    return this.headerCells().map((cell, position) => this.keyOf(cell, this.originalIndexOf(cell, position)));
  }

  private updateHiddenColumnKeys(): void {
    const keys: string[] = [];
    this.columnKeyMap().forEach((original, key) => {
      if (this.hiddenColumns.has(original)) {
        keys.push(key);
      }
    });
    this.hiddenColumnKeys = keys;
  }

  /** Rebuild the context menu's visibility toggles (hideable columns, in visual order). */
  private refreshHideableColumns(): void {
    this.hideableColumns = this.headerCells()
      .map((cell, position) => ({ cell, original: this.originalIndexOf(cell, position) }))
      .filter(({ cell }) => this.columnFlag(cell, 'hideable'))
      .map(({ cell, original }) => ({
        key: this.keyOf(cell, original),
        label: cell.textContent?.trim() ?? '',
        visible: !this.hiddenColumns.has(original)
      }));
  }

  private applyColumnLayoutToAll(): void {
    const expected = this.columnOrder.length || this.headerCells().length;
    const rows = this.tableElement?.querySelectorAll(':scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr') ?? [];
    rows.forEach(row => this.applyColumnLayout(row, expected));
  }

  /**
   * Reorder a row's cells to the current column order and reflect hidden columns
   * (`data-col-hidden`, hidden by the element's stylesheet). Rows whose cell count
   * differs (colspan summary rows) keep their authored layout.
   */
  private applyColumnLayout(row: Element, expected = this.columnOrder.length || this.headerCells().length): void {
    const cells = Array.from(row.children);
    if (!expected || cells.length !== expected) {
      return;
    }
    if (!cells.some(cell => cell.hasAttribute('data-col-index'))) {
      if (!this.columnOrder.length && !this.hiddenColumns.size) {
        return; // nothing was ever permuted or hidden - leave the row unstamped
      }
      // A fresh repeater row is born in authored order; stamp it so re-applying stays idempotent.
      cells.forEach((cell, index) => cell.setAttribute('data-col-index', String(index)));
    }
    const byOriginal = new Map(cells.map(cell => [Number(cell.getAttribute('data-col-index')), cell]));
    byOriginal.forEach((cell, original) => cell.toggleAttribute('data-col-hidden', this.hiddenColumns.has(original)));
    if (!this.columnOrder.length) {
      return;
    }
    const sequence = this.columnOrder.map(index => byOriginal.get(index));
    if (sequence.some(cell => cell === undefined) || sequence.every((cell, index) => cell === cells[index])) {
      return;
    }
    for (const cell of sequence) {
      row.appendChild(cell!);
    }
  }

  /** Rows rendered after a reorder/hide (paging, sorting, virtualization) are re-laid-out;
   *  a re-rendered header or table triggers a full re-resolve (incl. handle injection). */
  private onRowsMutated(records: MutationRecord[]): void {
    const table = this.tableElement;
    if (!table) {
      return;
    }
    let tableChanged = false;
    for (const record of records) {
      for (let index = 0; index < record.addedNodes.length; index++) {
        const node = record.addedNodes[index];
        if (!(node instanceof Element)) {
          continue;
        }
        if (node instanceof HTMLTableRowElement && node.closest('table') === table) {
          if (node.parentElement?.tagName === 'THEAD') {
            tableChanged = true;
          } else {
            this.applyColumnLayout(node);
          }
        } else if (node.contains(table)) {
          // The consumer re-rendered the table (e.g. an if.bind around it).
          tableChanged = true;
        }
      }
    }
    if (tableChanged) {
      this.refreshColumns();
    }
  }

  private setsEqual(a: Set<number>, b: Set<number>): boolean {
    return a.size === b.size && [...a].every(value => b.has(value));
  }

  private readStoredSettings(): StoredColumnSettings {
    if (this.userSettings) {
      return this.userSettings;
    }
    if (!this.storageKey) {
      return {};
    }
    try {
      const value = localStorage.getItem(this.storageId);
      const parsed: unknown = value ? JSON.parse(value) : undefined;
      return parsed && typeof parsed === 'object' ? parsed as StoredColumnSettings : {};
    } catch {
      return {};
    }
  }

  private persistSettings(): void {
    if (this.userSettings) {
      // DB-backed user tier: the owner persists the diff (e.g. per customer).
      this.emit<IColumnSettings>('column-user-settings-changed', this.computeUserSettingsDiff());
      return;
    }
    if (!this.storageKey) {
      return;
    }
    try {
      const settings = this.computeUserSettingsDiff();
      if (Object.keys(settings).length) {
        localStorage.setItem(this.storageId, JSON.stringify(settings));
      } else {
        localStorage.removeItem(this.storageId);
      }
    } catch {
      // Ignore unavailable storage.
    }
  }

  /** The user tier is only diffs against the designed defaults; a user at the designed
   *  configuration has an empty diff. */
  private computeUserSettingsDiff(): StoredColumnSettings {
    const settings: StoredColumnSettings = {};
    if (Object.keys(this.userSizes).length) {
      settings.widths = this.userSizes;
    }
    if (this.columnOrder.length && !this.ordersEqual(this.columnOrder, this.effectiveDefaultOrder())) {
      settings.order = this.columnOrderKeys();
    }
    // An explicit empty array records that the user unhid a default-hidden column.
    if (!this.setsEqual(this.hiddenColumns, this.defaultHidden)) {
      settings.hidden = this.hiddenColumnKeys;
    }
    if (Object.keys(this.userCaptions).length) {
      settings.captions = this.userCaptions;
    }
    return settings;
  }

  private emit<T>(name: string, detail: T): void {
    this.element.dispatchEvent(new CustomEvent<T>(name, { bubbles: true, detail }));
  }

  private get storageId(): string {
    return `dyn-table:${this.storageKey}`;
  }
}
